import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState(i18n.language);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle language
  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
    setLanguage(newLang);
  };

  return (
    <NavbarContainer isScrolled={isScrolled}>
      <NavbarContent>
        <LogoContainer to="/">
          <Logo src="/assets/icon.png" alt="Xenya" />
          <LogoText>Xenya</LogoText>
        </LogoContainer>

        {/* Desktop Navigation */}
        <DesktopNavLinks>
          <NavItem to="/">{t('navigation.home')}</NavItem>
          <NavItem to="/affiliate">{t('navigation.affiliate.title')}</NavItem>
          <NavItem to="/terms">{t('navigation.terms')}</NavItem>
          <NavItem to="/privacy">{t('navigation.privacy')}</NavItem>
          <NavItem to="/eula">{t('navigation.eula')}</NavItem>
          <NavItem to="/rgpd">{t('navigation.rgpd')}</NavItem>
          <NavItem to="/support">{t('navigation.support')}</NavItem>
        </DesktopNavLinks>

        <NavActions>
          <LanguageButton onClick={toggleLanguage}>
            {language === 'fr' ? 'EN' : 'FR'}
          </LanguageButton>
          <AppButton href="https://app.xenya.com">App</AppButton>
          <MobileMenuButton 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <Hamburger isOpen={isMobileMenuOpen} />
          </MobileMenuButton>
        </NavActions>
      </NavbarContent>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileNav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavItem 
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.home')}
            </MobileNavItem>
            <MobileNavItem 
              to="/affiliate"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.affiliate.title')}
            </MobileNavItem>
            <MobileNavItem 
              to="/terms"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.terms')}
            </MobileNavItem>
            <MobileNavItem 
              to="/privacy"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.privacy')}
            </MobileNavItem>
            <MobileNavItem 
              to="/eula"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.eula')}
            </MobileNavItem>
            <MobileNavItem 
              to="/rgpd"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.rgpd')}
            </MobileNavItem>
            <MobileNavItem 
              to="/support"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('navigation.support')}
            </MobileNavItem>
          </MobileNav>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

// Styled Components
const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndices.sticky};
  background: ${({ isScrolled, theme }) => 
    isScrolled 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'transparent'
  };
  backdrop-filter: ${({ isScrolled }) => 
    isScrolled ? 'blur(10px)' : 'none'
  };
  transition: ${({ theme }) => theme.transitions.default};
  padding: ${({ isScrolled }) => 
    isScrolled ? '10px 0' : '20px 0'
  };
  box-shadow: ${({ isScrolled, theme }) => 
    isScrolled ? theme.shadows.md : 'none'
  };
`;

const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.light};
  margin-left: 10px;
`;

const DesktopNavLinks = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin: 0 15px;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background: ${({ theme }) => theme.colors.accent};
    transition: ${({ theme }) => theme.transitions.default};
  }
  
  &:hover, &.active {
    color: ${({ theme }) => theme.colors.accent};
    
    &:after {
      width: 100%;
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
`;

const LanguageButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  margin-right: 15px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const AppButton = styled.a`
  background: ${({ theme }) => theme.colors.gradientSecondary};
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: 8px 20px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: none;
  padding: 5px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const Hamburger = styled.div`
  width: 24px;
  height: 18px;
  position: relative;
  
  &:before, &:after, span {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.light};
    transition: ${({ theme }) => theme.transitions.default};
  }
  
  &:before {
    top: ${({ isOpen }) => isOpen ? '8px' : '0'};
    transform: ${({ isOpen }) => isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
  
  span {
    top: 8px;
    opacity: ${({ isOpen }) => isOpen ? '0' : '1'};
  }
  
  &:after {
    bottom: ${({ isOpen }) => isOpen ? '8px' : '0'};
    transform: ${({ isOpen }) => isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
  }
`;

const MobileNav = styled(motion.div)`
  display: none;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
    flex-direction: column;
    padding: 20px;
    background: ${({ theme }) => theme.colors.dark};
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }
`;

const MobileNavItem = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  padding: 12px 0;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover, &.active {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

export default Navbar;